import {
  CreateVariables,
  FriendBulkTrashVariables,
  FriendUpdateVariables,
} from "./../models/app";
import { DataStore, SortDirection } from "aws-amplify";
import { useDispatch } from "react-redux";
import { setListing, setSelected } from "../store/ducks/friend";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import { Friend } from "../models";
import { FriendGetVariables, FriendListingVariables } from "../models/app";
import { CreateFriendInput } from "../models/GQL_API";
import { onCreateFriend } from "../graphql/subscriptions";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  async function fetch(params: FriendListingVariables) {
    const { searchText, startIndex, limit, customerID } = params;
    // stop using this table
    return [];
    try {
      const listing = await DataStore.query(
        Friend as any,
        (model: any) => {
          model.deleted("eq", "0");

          if (customerID) model.customerId("eq", customerID);

          if (searchText.length > 0)
            model.message("contains", searchText.toLowerCase());

          return model;
        },
        {
          page: startIndex / limit,
          limit: limit,
          sort: (s) => s.createdAt(SortDirection.DESCENDING),
        }
      );

      return listing;
    } catch (err: Error | any) {
      showError(err.message);
    }
  }

  async function get(params: FriendGetVariables) {
    const { id, listing } = params;

    try {
      const single: Friend | undefined =
        listing.length === 0
          ? await DataStore.query(Friend as any, id)
          : listing.find((model: Friend) => model.id === id);

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;

    try {
      const createInput: CreateFriendInput = {
        customerId: data.customerId ? data.customerId : "",
        friendId: data.friendId ? data.friendId : "",
        bookingID: data.bookingID ? data.bookingID : "",
        deleted: "0",
        createdAt: new Date().toLocaleString(),
        createdByID: userID,
        createdByName: userName,
      };

      await DataStore.save(new Friend(createInput as any));

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(params: FriendUpdateVariables) {
    const { id, listing, data } = params;
    try {
      const original = await get({ id, listing });

      await DataStore.save(
        Friend.copyOf(original!, (updated) => {
          updated.customerId = data.customerId
            ? data.customerId
            : original!.customerId;
          updated.friendId = data.friendId ? data.friendId : original!.friendId;
          updated.bookingID = data.bookingID
            ? data.bookingID
            : original!.bookingID;
        })
      );

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: FriendGetVariables) {
    try {
      const original = await get(params);

      await DataStore.save(
        Friend.copyOf(original!, (updated) => {
          updated.deleted = "1";
        })
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(params: FriendBulkTrashVariables) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: FriendGetVariables) {
    const { id, listing } = params;

    try {
      await DataStore.delete({ id, listing });

      dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "category",
      numeric: false,
      disablePadding: false,
      label: "Category",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["name", "category"];

  const api: any = {};

  api[`${listingName}Model`] = Friend as any;
  api[`${listingName}CreateSubscription`] = onCreateFriend;

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: Friend[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (id: string) =>
    dispatch(setSelected(id));

  return api;
};

export default useResource;
